/**
 * Landing Page Content Configuration
 * Centralized content management for easy updates
 */

export const LANDING_CONTENT = {
  hero: {
    eyebrow: 'The Employee-Generated Content Platform',
    titleStatic: 'Content from',
    rotatingWords: ['the floor', 'the field', 'the store', 'the call', 'the culture'],
    subheading: 'The simplest way for modern brands to activate employees as content creators and unlock the always-on stream of authentic, brand-safe social content.',
    cta: 'GET STARTED',
    // Decorative "content wall" shown beside the hero. Each frame is a real
    // employee screenshot with consistent overlay chrome (avatar + handle +
    // optional caption + likes). The avatar shows the business `logo` when one
    // is set; otherwise it falls back to a colored circle with the business
    // initial (`avatarLetter` / `avatarColor`) — used for businesses whose logo
    // is a wide wordmark that wouldn't read in a small circle. Leave `caption`
    // empty for clips that already have text baked into the image.
    frames: [
      {
        image: '/egc/mccabes-landing-1.webp',
        alt: 'McCabe’s employee dancing behind the bar',
        handle: '@mccabeswaterloo',
        logo: '/egc/mccabes-logo.webp',
        logoFit: 'contain' as const,
        avatarLetter: 'M',
        avatarColor: '#00a854',
        caption: '',
        likes: '247k',
      },
      {
        image: '/egc/big-league-movers-landing-1.webp',
        alt: 'POV from the cab of a Big League Movers truck on a country road',
        handle: '@bigleaguemovers',
        logo: '/egc/big-league-movers-logo.webp',
        avatarLetter: 'B',
        avatarColor: '#1a1a1a',
        caption: 'Out on the road',
        likes: '58.3k',
      },
      {
        image: '/egc/rock-island-tiki-bar-landing-1.webp',
        alt: 'Rock Island Tiki Bar server holding cash at the bar',
        handle: '@rockislandtikibar',
        logo: '/egc/rock-island-tiki-bar-logo.webp',
        avatarLetter: 'R',
        avatarColor: '#0e7490',
        caption: '',
        likes: '512k',
      },
      {
        image: '/egc/big-league-movers-landing-2.webp',
        alt: 'Big League Movers truck parked at a job site',
        handle: '@bigleaguemovers',
        logo: '/egc/big-league-movers-logo.webp',
        avatarLetter: 'B',
        avatarColor: '#1a1a1a',
        caption: 'Day in the life',
        likes: '94.7k',
      },
      {
        image: '/egc/merakai-beauty-bar-landing-1.webp',
        alt: 'Merakai Beauty Bar staff chatting at the front desk',
        handle: '@merakaibeautybar',
        logo: '/egc/merakai-beauty-bar-logo.webp',
        logoFit: 'contain' as const,
        avatarLetter: 'M',
        avatarColor: '#d4a017',
        caption: '',
        likes: '883k',
      },
      {
        image: '/egc/mccabes-landing-3.webp',
        alt: 'A Guinness being poured at McCabe’s',
        handle: '@mccabeswaterloo',
        logo: '/egc/mccabes-logo.webp',
        logoFit: 'contain' as const,
        avatarLetter: 'M',
        avatarColor: '#00a854',
        caption: 'The Perfect Pour',
        likes: '136k',
      },
    ],
  },

  heartbeat: {
    heading: 'Your people are your brand\'s heartbeat, and the key to unlocking content at scale.',
    body: [
      'Social platforms reward speed, volume, and authenticity.',
      'But most teams can\'t produce content fast enough. Your employees already hold the real advantage: the everyday moments that show your brand\'s culture in motion.',
      'CrewCast gives you a system to unlock and scale that advantage effortlessly.',
    ],
  },

  howItWorks: {
    heading: 'How It Works',
    steps: [
      {
        number: '1',
        icon: 'target',
        title: 'Set the Moment',
        description: 'You define what matters today: a product highlight, a behind-the-scenes clip, a customer story, a trending sound. Zero ambiguity.',
      },
      {
        number: '2',
        icon: 'camera',
        title: 'Your people capture it',
        description: 'Employees submit real, in-the-moment content straight from the floor, the field, the store, the call, the culture.',
      },
      {
        number: '3',
        icon: 'rocket',
        title: 'Moments to Momentum',
        description: 'Approve what works, publish with confidence, and build a content engine your team could never produce alone.',
      },
    ],
    tagline: 'Turn everyday Moments into your next content advantage.',
    cta: 'GET STARTED',
  },

  stats: {
    heading: 'Trusted by modern brands building content at the speed of culture.',
    metrics: [
      {
        value: '500M+',
        label: 'Impressions generated through employee-created Moments',
      },
      {
        value: '60%',
        label: 'Average increase in content output without adding headcount',
      },
      {
        value: '3-5x',
        label: 'Boost in social reach after activating employees',
      },
    ],
  },

  modernReality: {
    heading: 'Modern Social Reality',
    body: [
      'Audiences trust people more than companies. Algorithms boost human creators over corporate content.',
      'The advantage now belongs to companies that tap into their people - the ones closest to customers, culture, and real moments worth sharing.',
    ],
  },

  brands: {
    heading: 'Brands Winning with EGC',
    companies: [
      {
        name: 'Currys',
        logo: '/logos/currys.svg',
        link: '/blog/currys',
      },
      {
        name: 'No Frills',
        logo: '/logos/no-frills.svg',
        link: '/blog/no-frills',
      },
      {
        name: 'NeuroGum',
        logo: '/logos/neurogum.svg',
        link: '/blog/neurogum',
      },
      {
        name: 'Starbucks',
        logo: '/logos/starbucks.svg',
        link: '/blog/starbucks',
      },
      {
        name: 'SheerLuxe',
        logo: '/logos/sheerluxe.svg',
        link: '/blog/sheerluxe',
      },
      {
        name: 'Papa Johns',
        logo: '/logos/papajohns.svg',
        link: '/blog/papajohns',
      },
      {
        name: 'Philz Coffee',
        logo: '/logos/philz-coffee.svg',
        link: '/blog/philz-coffee',
      },
    ],
  },

  finalCta: {
    heading: 'Your employees are already living the moments your audience wants to see.',
    subheading: 'CrewCast turns those Moments into a scalable content engine you control.',
    cta: 'GET STARTED',
  },
};

